import { useCallback } from "react";
import type { RefObject } from "react";
import { useNavigate } from "react-router";
import { EmptyState, Icon, IconButton, PageHeader } from "@astrum/ui";
import type { Channel } from "../../lib/api.js";
import { useAuth } from "../../lib/auth.js";
import { useRealtime } from "../../lib/realtime-context.js";
import { useFileDrop } from "../../lib/useFileDrop.js";
import { BookmarkBar } from "./BookmarkBar.js";
import { Composer } from "./Composer.js";
import { MessageList } from "./MessageList.js";
import type { ChannelDetail, PendingMessage } from "./types.js";
import type { ChannelMessages } from "./useChannelMessages.js";
import type { FileUploads } from "./useFileUploads.js";

/** Renders the "X is typing" line. Kept separate so its frequent re-renders
 * don't touch the message list. */
function TypingIndicator({ names }: { names: string[] }) {
  if (names.length === 0) return null;

  const label =
    names.length === 1
      ? `${names[0]} is typing`
      : names.length === 2
        ? `${names[0]} and ${names[1]} are typing`
        : `${names[0]} and ${names.length - 1} others are typing`;

  return (
    <div className="chat-typing" aria-live="polite">
      <span className="chat-typing__dots" aria-hidden="true">
        <i />
        <i />
        <i />
      </span>
      {label}
    </div>
  );
}

interface ThreadPanelProps {
  channelId: string | undefined;
  /** The open channel's sidebar entry; undefined while the list is loading. */
  channel: Channel | undefined;
  detail: ChannelDetail | undefined;
  thread: ChannelMessages;
  scrollRef: RefObject<HTMLDivElement | null>;
  typingNames: string[];
  files: FileUploads;
  replyingTo: PendingMessage | null;
  onCancelReply: () => void;
  onSend: (body: string) => void;
  onCreateTask: () => void;
  searchOpen: boolean;
  detailsOpen: boolean;
  onToggleSearch: () => void;
  onToggleDetails: () => void;
}

/** The open conversation: its header, the message list, and the composer. */
export function ThreadPanel({
  channelId,
  channel,
  detail,
  thread,
  scrollRef,
  typingNames,
  files,
  replyingTo,
  onCancelReply,
  onSend,
  onCreateTask,
  searchOpen,
  detailsOpen,
  onToggleSearch,
  onToggleDetails,
}: ThreadPanelProps) {
  const navigate = useNavigate();
  const { viewer } = useAuth();
  const { notifyTyping } = useRealtime();
  const { attach } = files;
  // Bookmarks are managed by members, the same rule as posting.
  const isMember = !!viewer && (detail?.members.some((m) => m.id === viewer.id) ?? false);

  // Dropped files send one at a time, in order — a message carries exactly
  // one attachment (see useFileUploads), so several dropped files become
  // several messages rather than one that could only hold the first.
  const { isOver: isDraggingFileOverThread, dropZoneProps: threadDropZoneProps } = useFileDrop(
    useCallback((droppedFiles) => droppedFiles.forEach((file) => attach(file)), [attach]),
  );

  const isProjectChannel = channel?.kind === "project";
  const archived = !!(detail?.channel.archivedAt ?? channel?.archivedAt);

  /*
   * The detail's count wins over the list's.
   *
   * `memberCount` comes from the channel list, which is not re-fetched when
   * somebody is added or removed — only the detail is. Preferring the list
   * meant the header sat at "1 members" while the panel beside it correctly
   * listed four, which reads as the add having failed.
   */
  const memberCount = detail?.members.length ?? channel?.memberCount ?? 0;
  /* Who may @channel a large channel — the server's rule, mirrored so the
   * composer can say so before sending rather than after. */
  const canBroadcast =
    detail?.channel.canManage === true || detail?.members.some((m) => m.id === viewer?.id && m.role === "owner") === true;
  const headerSubtitle = channel ? `${memberCount} ${memberCount === 1 ? "member" : "members"}` : undefined;

  return (
    <section className="chat-thread" aria-label="Conversation">
      {!channelId ? (
        <div className="chat-thread__empty">
          <EmptyState icon="chat" title="Select a channel" description="Choose a conversation from the list to get started." />
        </div>
      ) : (
        <>
          <PageHeader
            className="chat-thread__header"
            /* Only reachable on a narrow screen, where the channel list is
               hidden; CSS keeps it out of the way on desktop. */
            leading={
              <button type="button" className="chat-thread__back" onClick={() => navigate("/chat")} aria-label="Back to channels">
                <Icon name="chevron-left" size={18} />
              </button>
            }
            title={
              <>
                {channel?.kind === "direct" || channel?.kind === "group" ? null : <em>#</em>}
                {channel?.name ?? "Loading…"}
              </>
            }
            meta={
              <>
                {headerSubtitle && <span className="chat-thread__subtitle">{headerSubtitle}</span>}
                {detail?.channel.projectId && isProjectChannel && (
                  <span className="chat-thread__subtitle chat-thread__subtitle--project">Linked to a project</span>
                )}
              </>
            }
            actions={
              <>
                <IconButton
                  icon={<Icon name="search" size={16} />}
                  label={searchOpen ? "Close search" : "Search this conversation"}
                  variant={searchOpen ? "soft" : "ghost"}
                  onClick={onToggleSearch}
                />

                {/* Any channel, not just a project one: members, topic, and the
                    manage actions apply to team channels too, and gating this on
                    a linked project made renaming or deleting one unreachable. */}
                {channel?.kind !== "direct" && (
                  <IconButton
                    icon={<Icon name="document" size={16} />}
                    label={detailsOpen ? "Hide details" : "Show details"}
                    variant={detailsOpen ? "soft" : "ghost"}
                    onClick={onToggleDetails}
                  />
                )}
              </>
            }
          />

          {channelId && <BookmarkBar channelId={channelId} canEdit={isMember} />}

          <div
            className={`chat-thread__drop-zone ${isDraggingFileOverThread ? "is-drop-target" : ""}`}
            {...threadDropZoneProps}
          >
            {isDraggingFileOverThread && (
              <div className="chat-thread__drop-overlay">
                <div className="chat-thread__drop-card">
                  <Icon name="attach" size={24} />
                  <span>Drop to share in this conversation</span>
                </div>
              </div>
            )}

            <MessageList
              scrollRef={scrollRef}
              messages={thread.messages}
              loading={thread.loading}
              loadingOlder={thread.loadingOlder}
              onLoadOlder={thread.loadOlder}
            />

            <TypingIndicator names={typingNames} />

            {archived ? (
              // Read-only: the server refuses posts, edits, reactions and uploads
              // here too, so the composer is not offered at all.
              <div className="chat-archived-banner" role="status">
                <Icon name="lock" size={14} />
                This channel is archived. It is read-only until it is restored.
              </div>
            ) : (
            <Composer
              key={channelId}
              draft={channelId ? { channelId, threadRootId: null } : undefined}
              channelKind={channel?.kind}
              placeholder={
                channel?.kind === "direct" || channel?.kind === "group" ? `Message ${channel.name}` : `Message #${channel?.name ?? ""}`
              }
              onSend={onSend}
              onTyping={() => channelId && notifyTyping(channelId)}
              files={files}
              replyingTo={replyingTo}
              onCancelReply={onCancelReply}
              members={detail?.members ?? []}
              canBroadcast={canBroadcast}
              onCreateTask={onCreateTask}
              onCreateEvent={() =>
                navigate(`/schedule?new=1${detail?.channel.projectId ? `&project=${detail.channel.projectId}` : ""}`)
              }
            />
            )}
          </div>
        </>
      )}
    </section>
  );
}
